import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sparkles, Zap } from 'lucide-react';

interface WelcomeAnimationProps {
  firstName: string;
  isFirstLogin: boolean;
  onComplete: () => void;
}

export function WelcomeAnimation({ firstName, isFirstLogin, onComplete }: WelcomeAnimationProps) {
  const [show, setShow] = useState(true);
  const doneRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setShow(false);
    window.setTimeout(onComplete, 500);
  }, [onComplete]);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      finish();
    }, 4000);

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [finish]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  const message = isFirstLogin 
    ? `Welcome to your Monitoring Universe ${firstName}`
    : `Welcome back ${firstName}`;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[10070] flex items-center justify-center bg-black/65 pointer-events-none"
        >
          {/* Holographic Background Effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Animated Gradient Orbs */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
                x: [0, 100, 0],
                y: [0, -50, 0],
              }}
              transition={{ duration: 8, repeat: Infinity }}
              className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-cyber-cyan/30 via-cyber-blue/20 to-transparent blur-3xl"
            />
            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.3, 0.5, 0.3],
                x: [0, -100, 0],
                y: [0, 50, 0],
              }}
              transition={{ duration: 10, repeat: Infinity }}
              className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-cyber-purple/30 via-cyber-blue/20 to-transparent blur-3xl"
            />
            
            {/* Particle Effects */}
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: Math.random() * window.innerHeight,
                  scale: 0,
                  opacity: 0,
                }}
                animate={{
                  scale: [0, 1, 0],
                  opacity: [0, 0.6, 0],
                  y: [null, Math.random() * -200],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: Math.random() * 3,
                }}
                className="absolute w-1 h-1 bg-cyber-cyan rounded-full"
              />
            ))}
          </div>

          {/* Main Content — only this region receives clicks */}
          <div className="relative z-10 pointer-events-auto max-w-[calc(100vw-2rem)]">
            {/* Glassmorphism Container */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: -20 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="relative"
            >
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyber-cyan/20 via-cyber-blue/20 to-cyber-purple/20 blur-2xl" />
              
              {/* Glass Card */}
              <div className="relative bg-dark-800/80 backdrop-blur-xl border border-cyber-cyan/30 rounded-2xl p-12 shadow-2xl">
                {/* Holographic Border Animation */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden">
                  <motion.div
                    animate={{
                      rotate: 360,
                    }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="absolute -inset-1 bg-gradient-to-r from-cyber-cyan via-cyber-blue to-cyber-purple opacity-20 blur-sm"
                  />
                </div>

                <div className="relative z-10 text-center space-y-6 max-w-2xl">
                  {/* Icon */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="flex justify-center"
                  >
                    <div className="relative">
                      <motion.div
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.5, 0.8, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                        }}
                        className="absolute inset-0 bg-cyber-cyan/30 rounded-full blur-xl"
                      />
                      <div className="relative w-24 h-24 bg-gradient-to-br from-cyber-cyan to-cyber-blue rounded-full flex items-center justify-center">
                        <Sparkles className="w-12 h-12 text-white" />
                      </div>
                    </div>
                  </motion.div>

                  {/* Welcome Text */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    <h1 className="text-5xl font-bold bg-gradient-to-r from-cyber-cyan via-cyber-blue to-cyber-purple bg-clip-text text-transparent leading-tight">
                      {message}
                    </h1>
                  </motion.div>

                  {/* Subtitle */}
                  {isFirstLogin && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.6, delay: 0.8 }}
                      className="text-xl text-cyber-cyan/80 flex items-center justify-center gap-2"
                    >
                      <Zap className="w-5 h-5" />
                      AI Control Interface Activated
                    </motion.p>
                  )}

                  {/* Holographic Line Effect */}
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1, delay: 1 }}
                    className="h-px bg-gradient-to-r from-transparent via-cyber-cyan to-transparent"
                  />

                  {/* Loading Animation */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 1.2 }}
                    className="flex flex-col items-center justify-center gap-3"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div className="flex gap-1">
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={i}
                            animate={{
                              scale: [1, 1.5, 1],
                              opacity: [0.5, 1, 0.5],
                            }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              delay: i * 0.2,
                            }}
                            className="w-2 h-2 bg-cyber-cyan rounded-full"
                          />
                        ))}
                      </div>
                      <span className="text-sm text-gray-400">Initializing COMO...</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => finish()}
                      className="text-sm font-medium text-cyber-cyan hover:text-white underline-offset-2 hover:underline"
                    >
                      Continue
                    </button>
                    <p className="text-[11px] text-gray-500">Press Esc to skip this screen</p>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}